// Pure, DOM-free helpers for the offline scanning cache. Kept out of the
// Stimulus controllers so the storage logic is reusable and easy to reason
// about. No tests (repo has no JS runner) — keep it small and obvious.

const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7-day expiry (matches magic-link TTL)

const bundleKey = (performanceId) => `ntry.scan.bundle.${performanceId}`;
const queueKey = (performanceId) => `ntry.scan.queue.${performanceId}`;

// Aztec codes may arrive with stray whitespace / lower case (camera, paste,
// hardware wedge). Normalise before every lookup so all paths match the same way.
const normalizeCode = (raw) => raw.replace(/\s/g, "").toUpperCase();

export function saveBundle(performanceId, tickets) {
  const value = { downloadedAt: Date.now(), tickets };
  localStorage.setItem(bundleKey(performanceId), JSON.stringify(value));
  return value;
}

// Returns the cached bundle, or null if absent or older than the TTL. Expiry is
// best-effort: it's evaluated whenever the bundle is read.
export function loadBundle(performanceId) {
  const raw = localStorage.getItem(bundleKey(performanceId));
  if (!raw) return null;
  const value = JSON.parse(raw);
  if (Date.now() - value.downloadedAt > TTL_MS) {
    localStorage.removeItem(bundleKey(performanceId));
    return null;
  }
  return value;
}

export function loadQueue(performanceId) {
  return JSON.parse(localStorage.getItem(queueKey(performanceId)) || "[]");
}

export function enqueueScan(performanceId, code, scannedAt) {
  const queue = loadQueue(performanceId);
  queue.push({ code, scannedAt });
  localStorage.setItem(queueKey(performanceId), JSON.stringify(queue));
  return queue;
}

export function clearQueue(performanceId) {
  localStorage.removeItem(queueKey(performanceId));
}

// Mirrors the server's scan_outcome (scans_controller.rb): unknown / invalid /
// already_scanned / wrong_category / valid. `selectedTypeIds` are numbers.
// Mutates the cached entry to "scanned" on a valid scan (preserving the
// bundle's downloadedAt so the 7-day clock isn't reset), then queues it.
export function verifyLocally(performanceId, rawCode, selectedTypeIds) {
  const code = normalizeCode(rawCode);
  const bundle = loadBundle(performanceId);
  if (!bundle) return { outcome: "unknown", ticket: null };

  const entry = bundle.tickets.find((t) => t.code === code);
  if (!entry) return { outcome: "unknown", ticket: null };

  const ticket = { name: entry.n, persons: entry.p, ticket_type_id: entry.tt, ticket_type: entry.tn };
  if (entry.st === "scanned") return { outcome: "already_scanned", ticket };
  if (entry.st !== "new") return { outcome: "invalid", ticket };
  if (!selectedTypeIds.includes(entry.tt)) return { outcome: "wrong_category", ticket };

  const scannedAt = new Date().toISOString();
  // NOTE: the bundle write and the queue write below are not atomic. A crash
  // between them leaves the ticket marked scanned locally but absent from the
  // sync queue (a missed sync, not a double-scan). Acceptable for now.
  entry.st = "scanned";
  localStorage.setItem(bundleKey(performanceId), JSON.stringify(bundle));
  enqueueScan(performanceId, code, scannedAt);

  return { outcome: "valid", ticket, code, scannedAt };
}

// Cached scan state for a code ("new" | "scanned" | other), or null if the code
// isn't in the bundle (or there's no bundle). The online path uses this to catch
// a ticket this device already scanned offline but hasn't synced yet — the
// server would still report it "valid", but locally we know it was admitted.
export function localScanState(performanceId, rawCode) {
  const bundle = loadBundle(performanceId);
  if (!bundle) return null;
  const entry = bundle.tickets.find((t) => t.code === normalizeCode(rawCode));
  return entry ? entry.st : null;
}

// Mark a code scanned in the cached bundle WITHOUT queuing it — for scans the
// server already recorded (online mode). Mirrors verifyLocally's write-back so a
// later offline scan of the same code returns already_scanned instead of
// re-admitting it. No-op if the code is absent or not "new"; preserves
// downloadedAt so the 7-day clock isn't reset.
export function markScannedLocally(performanceId, rawCode) {
  const bundle = loadBundle(performanceId);
  if (!bundle) return;
  const entry = bundle.tickets.find((t) => t.code === normalizeCode(rawCode));
  if (entry && entry.st === "new") {
    entry.st = "scanned";
    localStorage.setItem(bundleKey(performanceId), JSON.stringify(bundle));
  }
}
